import { useEffect, useState, type SyntheticEvent } from 'react';

const SAMPLE_SIZE = 32;

interface Params {
  imageUrl: string | undefined;
  minHeight: number;
  maxHeight: number;
}

interface UseImageEdgeBackgroundResult {
  boxRef: (element: HTMLElement | null) => void;
  background: string | undefined;
  height: number;
  handleImageLoad: (event: SyntheticEvent<HTMLImageElement>) => void;
}

interface ImageEdges {
  imageUrl: string;
  aspectRatio: number;
  background: string;
}

const averageColor = (pixels: Uint8ClampedArray) => {
  let red = 0;
  let green = 0;
  let blue = 0;
  const pixelCount = pixels.length / 4;

  for (let index = 0; index < pixels.length; index += 4) {
    red += pixels[index];
    green += pixels[index + 1];
    blue += pixels[index + 2];
  }

  return `rgb(${Math.round(red / pixelCount)} ${Math.round(green / pixelCount)} ${Math.round(blue / pixelCount)})`;
};

export const useImageEdgeBackground = ({
  imageUrl,
  minHeight,
  maxHeight,
}: Params): UseImageEdgeBackgroundResult => {
  const [box, setBox] = useState<HTMLElement | null>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  const [imageEdges, setImageEdges] = useState<ImageEdges>();

  const isCurrentImage = !!imageUrl && imageEdges?.imageUrl === imageUrl;
  const fullWidthHeight = isCurrentImage
    ? boxWidth / imageEdges.aspectRatio
    : maxHeight;
  const height = Math.min(maxHeight, Math.max(minHeight, fullWidthHeight));
  const background = isCurrentImage ? imageEdges.background : undefined;

  const handleImageLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    const context = document
      .createElement('canvas')
      .getContext('2d', { willReadFrequently: true });

    if (!imageUrl || !context) {
      return;
    }

    context.canvas.width = SAMPLE_SIZE;
    context.canvas.height = SAMPLE_SIZE;
    context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);

    const top = averageColor(context.getImageData(0, 0, SAMPLE_SIZE, 1).data);
    const bottom = averageColor(
      context.getImageData(0, SAMPLE_SIZE - 1, SAMPLE_SIZE, 1).data,
    );

    setImageEdges({
      imageUrl,
      aspectRatio: image.naturalWidth / image.naturalHeight,
      background: `linear-gradient(to bottom, ${top}, ${bottom})`,
    });
  };

  useEffect(() => {
    if (!box) {
      return;
    }

    const observer = new ResizeObserver(() => setBoxWidth(box.clientWidth));

    observer.observe(box);

    return () => observer.disconnect();
  }, [box]);

  return { boxRef: setBox, background, height, handleImageLoad };
};
